import { useMemo, useRef, useEffect } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import chartData from '../../data/chartData.json'
import {
  chartColors,
  barDefaults,
  mergeOptions,
  getEfficiencyColor,
  withOpacity,
  createGradient
} from '../../theme/chartTheme'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

function EfficiencyDistribution({ data: filterData, onBarClick }) {
  const chartRef = useRef(null)

  // Use filtered data if provided, otherwise use default chart data
  const values = useMemo(() => {
    if (filterData && filterData.length > 0) {
      return filterData
        .filter(d => d.efficiency_pct != null)
        .map(d => d.efficiency_pct)
    }
    return chartData.efficiency_distribution.values
  }, [filterData])

  // Create histogram buckets
  const buckets = [
    { label: '0-10%', min: 0, max: 10 },
    { label: '10-15%', min: 10, max: 15 },
    { label: '15-20%', min: 15, max: 20 },
    { label: '20-25%', min: 20, max: 25 },
    { label: '25-30%', min: 25, max: 30 },
    { label: '30-40%', min: 30, max: 40 },
    { label: '40-50%', min: 40, max: 50 },
    { label: '50%+', min: 50, max: 100 }
  ]

  const counts = buckets.map(bucket =>
    values.filter(v => v >= bucket.min && v < bucket.max).length
  )

  // Color bars based on efficiency range (performance-based coloring)
  const barColors = buckets.map(bucket => {
    const midpoint = (bucket.min + bucket.max) / 2
    return getEfficiencyColor(midpoint)
  })

  const data = {
    labels: buckets.map(b => b.label),
    datasets: [
      {
        label: 'Number of Measurements',
        data: counts,
        backgroundColor: barColors.map(c => withOpacity(c, 0.85)),
        hoverBackgroundColor: barColors,
        borderColor: barColors,
        borderWidth: 0,
        borderRadius: barDefaults.borderRadius,
        barPercentage: 0.85,
        categoryPercentage: 0.9
      }
    ]
  }

  const options = mergeOptions({
    onClick: (event, elements) => {
      if (elements.length > 0 && onBarClick) {
        const index = elements[0].index
        const bucket = buckets[index]
        onBarClick('efficiencyRange', `${bucket.min}-${bucket.max}`)
      }
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default'
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          title: (context) => {
            const bucket = buckets[context[0].dataIndex]
            return `Efficiency: ${bucket.label}`
          },
          label: (context) => {
            return `${context.raw} measurements`
          },
          afterLabel: (context) => {
            const total = values.length
            const percentage = ((context.raw / total) * 100).toFixed(1)
            return `${percentage}% of total`
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Count',
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 12,
            weight: 600
          },
          color: chartColors.textSecondary
        },
        grid: {
          color: chartColors.grid
        },
        ticks: {
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 11
          },
          color: chartColors.textSecondary
        }
      },
      x: {
        title: {
          display: true,
          text: 'Efficiency Improvement Range',
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 12,
            weight: 600
          },
          color: chartColors.textSecondary
        },
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 11
          },
          color: chartColors.textSecondary
        }
      }
    }
  })

  return <Bar ref={chartRef} data={data} options={options} />
}

export default EfficiencyDistribution
